import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { getCachedUser } from '../lib/auth'
import ClaimPrompt from '../components/ClaimPrompt'
import LensBar from '../components/LensBar'
import FanChart from '../components/FanChart'
import { useI18n } from '../lib/i18n'
import CapacityMeter from '../components/CapacityMeter'
import { selectSubgraph, type LensKind } from '../lib/lens'
import { findMeCandidates } from '../components/ClaimPrompt'
import SeedNudge from '../components/SeedNudge'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { layoutTree, NODE_W, NODE_H } from '../lib/graph'
import type { TreeGraph, Person } from '../lib/types'
import { setSageContext, clearSageContext } from '../lib/sagebus'
import GalleryTab from '../components/GalleryTab'
import ChronicleTab from '../components/ChronicleTab'
import AtlasTab from '../components/AtlasTab'
import StoriesSection from '../components/StoriesSection'
import { findRelation } from '../lib/kinship'
import LinkKinPanel from '../components/LinkKinPanel'
import { maskName, getVeil, subscribeSecurity } from '../lib/security'
import TreeSettings from '../components/TreeSettings'

type RelKind = 'parent-of' | 'child-of' | 'spouse-of' | 'sibling-of'
type Tab = 'graph' | 'chronicle' | 'gallery' | 'atlas'

interface PersonForm {
  first_name: string
  last_name: string
  maiden_name: string
  alias: string
  gender: string
  birth_date: string
  birth_place: string
  death_date: string
  occupation: string
  bio: string
  is_living: boolean
  burial_cemetery: string
  burial_plot: string
  burial_place: string
  burial_notes: string
}

const EMPTY_FORM: PersonForm = {
  first_name: '', last_name: '', maiden_name: '', alias: '', gender: '', birth_date: '', birth_place: '',
  death_date: '', occupation: '', bio: '', is_living: true,
  burial_cemetery: '', burial_plot: '', burial_place: '', burial_notes: '',
}

const GENDER_RING: Record<string, string> = {
  male: '#60a5fa', female: '#f472b6', other: '#a78bfa',
}

/** Propose linking a person to a CIRCLE MEMBER's account (identities already visible; keepers approve). */
function LinkAccountButton({ treeId, personId }: { treeId: string; personId: string }) {
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<{ id: string; display: string }[]>([])
  const [msg, setMsg] = useState('')
  const toggle = async () => {
    if (!open) {
      try { setUsers((await api.linkableUsers(treeId)).users) } catch { setUsers([]) }
    }
    setOpen(o => !o); setMsg('')
  }
  const propose = async (uid: string) => {
    try {
      const r = await api.fileClaim({ tree_id: treeId, kind: 'link', person_id: personId, target_user_id: uid })
      setMsg(r.message); setOpen(false)
    } catch (e) { setMsg(e instanceof Error && e.message.includes('409') ? 'Already pending or linked.' : 'Could not propose the link.') }
  }
  return (
    <span className="relative">
      <button onClick={toggle} className="text-[11px] px-2.5 py-1.5 rounded-lg border border-white/15 text-slate-300 hover:border-gold-500/30">
        🔗 Link to account…
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 w-52 card-glass !bg-void-900/95 p-1.5 max-h-48 overflow-y-auto">
          {users.map(u => (
            <button key={u.id} onClick={() => propose(u.id)}
              className="w-full text-start text-xs px-2 py-1.5 rounded-lg text-slate-300 hover:bg-gold-500/10">{u.display}</button>
          ))}
          {users.length === 0 && <p className="text-[10px] text-slate-600 px-2 py-1.5">No circle members to link — invite kin to a circle first.</p>}
        </div>
      )}
      {msg && <p className="text-[10px] text-gold-300 mt-1">{msg}</p>}
    </span>
  )
}

export default function TreeView() {
  const { id } = useParams<{ id: string }>()
  const myUserId = getCachedUser()?.id
  const [lens, setLens] = useState<LensKind>(() => (localStorage.getItem(`fa_lens_${window.location.pathname}`) as LensKind) || 'everyone')
  const [collaterals, setCollaterals] = useState(() => localStorage.getItem('fa_lens_collaterals') === '1')
  const { t } = useI18n()
  const [focusOverride, setFocusOverride] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [graph, setGraph] = useState<TreeGraph | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('graph')
  const [selected, setSelected] = useState<Person | null>(null)
  const [formMode, setFormMode] = useState<null | { relation?: { type: RelKind; personId: string; label: string }; editing?: Person }>(null)
  const [form, setForm] = useState<PersonForm>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [dupNotice, setDupNotice] = useState('')
  const [saveError, setSaveError] = useState('')
  const [linking, setLinking] = useState(false)
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 1200, h: 800 })
  // constellation or fan — a view preference, not a data one, so it lives locally
  const [view, setView] = useState<'constellation' | 'fan'>(
    () => (localStorage.getItem('fa_view') === 'fan' ? 'fan' : 'constellation')
  )
  const [isMobile, setIsMobile] = useState(false)
  const [veil, setVeil] = useState(getVeil())
  useEffect(() => subscribeSecurity(() => setVeil(getVeil())), [])
  const svgRef = useRef<SVGSVGElement>(null)
  const avatarRef = useRef<HTMLInputElement>(null)
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, y: 0, vx: 0, vy: 0 })
  const lastTouchDist = useRef<number | null>(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const load = useCallback(async (fit = false) => {
    if (!id) return
    const g = await api.getTree(id)
    setGraph(g)
    setLoading(false)
    if (fit) fitView(g)
    setSelected(prev => prev ? (g.persons.find(p => p.id === prev.id) || null) : null)
  }, [id])

  useEffect(() => { load(true) }, [load])
  useEffect(() => { setLinking(false) }, [selected?.id])

  // ?person= deep-link (from the ⌘K palette): select once graph is loaded
  useEffect(() => {
    const pid = searchParams.get('person')
    if (pid && graph) {
      const p = graph.persons.find(x => x.id === pid)
      if (p) { setSelected(p); setTab('graph') }
      setSearchParams({}, { replace: true })
    }
  }, [graph, searchParams, setSearchParams])

  // Register this tree with the Sage dock (context + live refresh for MCP actions)
  useEffect(() => {
    if (id && graph) {
      setSageContext({ treeId: id, treeName: graph.tree.name, refresh: () => load() })
    }
    return () => clearSageContext()
  }, [id, graph?.tree.name, load]) // eslint-disable-line react-hooks/exhaustive-deps

  // the lens filters the graph; layoutTree renders whatever it is handed, so every
  // existing layout invariant keeps holding unchanged (D-124)
  const me = getCachedUser()
  const focusId = useMemo(() => {
    if (!graph) return undefined
    const claimed = graph.persons.find(p => p.claimed_by && p.claimed_by === myUserId)
    if (claimed) return claimed.id
    const guess = findMeCandidates(graph.persons, me?.name, me?.email)[0]
    if (guess) return guess.id
    return selected?.id          // otherwise centre on whoever you have open
  }, [graph, myUserId, me?.name, me?.email, selected?.id])

  // an explicitly chosen person always wins — this is what lets a lens mean
  // "my wife's father's line" rather than only ever "mine" (D-124)
  const activeId = focusOverride ?? focusId
  const lensed = useMemo(
    () => graph ? selectSubgraph(graph.persons, graph.relationships, { kind: lens, focusId: activeId, collaterals }) : null,
    [graph, lens, activeId, collaterals]
  )
  const layout = useMemo(
    () => lensed ? layoutTree(lensed.persons, lensed.relationships) : null,
    [lensed]
  )

  const role = graph?.role || 'viewer'
  const canEdit = role === 'owner' || role === 'editor'

  function fitView(g: TreeGraph) {
    const l = layoutTree(g.persons, g.relationships)
    const pad = 60
    setViewBox({
      x: -pad, y: -pad,
      w: Math.max(l.width + pad * 2, 900),
      h: Math.max(l.height + pad * 2, 600),
    })
  }

  const selectedRels = useMemo(() => {
    type Kin = { person: Person; relId: string }
    if (!selected || !graph) return { parents: [] as Kin[], children: [] as Kin[], spouses: [] as Kin[], siblings: [] as Kin[] }
    const byId = new Map(graph.persons.map(p => [p.id, p]))
    const parents: Kin[] = [], children: Kin[] = [], spouses: Kin[] = []
    const myParentIds = new Set<string>()
    for (const r of graph.relationships) {
      if (r.type === 'parent') {
        if (r.person2_id === selected.id && byId.has(r.person1_id)) { parents.push({ person: byId.get(r.person1_id)!, relId: r.id }); myParentIds.add(r.person1_id) }
        if (r.person1_id === selected.id && byId.has(r.person2_id)) children.push({ person: byId.get(r.person2_id)!, relId: r.id })
      } else {
        if (r.person1_id === selected.id && byId.has(r.person2_id)) spouses.push({ person: byId.get(r.person2_id)!, relId: r.id })
        if (r.person2_id === selected.id && byId.has(r.person1_id)) spouses.push({ person: byId.get(r.person1_id)!, relId: r.id })
      }
    }
    // siblings = anyone (else) who shares at least one parent (derived, not a stored edge)
    const sibIds = new Set<string>()
    for (const r of graph.relationships) {
      if (r.type === 'parent' && myParentIds.has(r.person1_id) && r.person2_id !== selected.id && byId.has(r.person2_id)) {
        sibIds.add(r.person2_id)
      }
    }
    const siblings: Kin[] = [...sibIds].map(id => ({ person: byId.get(id)!, relId: `sib-${id}` }))
    return { parents, children, spouses, siblings }
  }, [selected, graph])

  const unlink = async (relId: string, otherName: string) => {
    if (!confirm(`Remove the bond with ${otherName}? (The person stays in the tree.)`)) return
    await api.deleteRelationship(relId)
    await load()
  }

  const exportSvg = () => {
    const svg = svgRef.current
    if (!svg || !graph) return
    const clone = svg.cloneNode(true) as SVGSVGElement
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    clone.setAttribute('style', 'background: #0d0a1c')
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${graph.tree.name.replace(/[^\w-]+/g, '_')}-constellation.svg`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // ── pan / zoom ──
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const factor = e.deltaY > 0 ? 1.12 : 0.89
    setViewBox(v => {
      const cx = v.x + v.w / 2, cy = v.y + v.h / 2
      return { x: cx - v.w * factor / 2, y: cy - v.h * factor / 2, w: v.w * factor, h: v.h * factor }
    })
  }, [])

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return
    isPanning.current = true
    panStart.current = { x: e.clientX, y: e.clientY, vx: viewBox.x, vy: viewBox.y }
  }, [viewBox])

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const dx = (e.clientX - panStart.current.x) * (viewBox.w / rect.width)
    const dy = (e.clientY - panStart.current.y) * (viewBox.h / rect.height)
    setViewBox(v => ({ ...v, x: panStart.current.vx - dx, y: panStart.current.vy - dy }))
  }, [viewBox])

  const onMouseUp = useCallback(() => { isPanning.current = false }, [])

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      lastTouchDist.current = Math.hypot(dx, dy)
    } else if (e.touches.length === 1) {
      isPanning.current = true
      panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, vx: viewBox.x, vy: viewBox.y }
    }
  }, [viewBox])

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.hypot(dx, dy)
      const factor = lastTouchDist.current / dist
      lastTouchDist.current = dist
      setViewBox(v => {
        const cx = v.x + v.w / 2, cy = v.y + v.h / 2
        return { x: cx - v.w * factor / 2, y: cy - v.h * factor / 2, w: v.w * factor, h: v.h * factor }
      })
    } else if (e.touches.length === 1 && isPanning.current && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect()
      const dx = (e.touches[0].clientX - panStart.current.x) * (viewBox.w / rect.width)
      const dy = (e.touches[0].clientY - panStart.current.y) * (viewBox.h / rect.height)
      setViewBox(v => ({ ...v, x: panStart.current.vx - dx, y: panStart.current.vy - dy }))
    }
  }, [viewBox])

  const onTouchEnd = useCallback(() => { isPanning.current = false; lastTouchDist.current = null }, [])

  // ── person create / edit ──
  const openAdd = (relation?: { type: RelKind; personId: string; label: string }) => {
    setForm(EMPTY_FORM)
    setFormMode({ relation })
  }

  /** A gap in the fan is a question with a known answer shape: this person's father,
   *  or this person's mother. Open the form already knowing which. */
  const addParentFor = (child: Person, side: 'father' | 'mother') => {
    setForm({ ...EMPTY_FORM, gender: side === 'father' ? 'male' : 'female' })
    setFormMode({ relation: { type: 'parent-of', personId: child.id, label: `${side} of ${maskName(child, veil).split(' ')[0]}` } })
  }

  const openEdit = (p: Person) => {
    setForm({
      first_name: p.first_name, last_name: p.last_name || '', maiden_name: p.maiden_name || '', alias: p.alias || '',
      gender: p.gender || '',
      birth_date: p.birth_date || '', birth_place: p.birth_place || '',
      death_date: p.death_date || '', occupation: p.occupation || '', bio: p.bio || '',
      is_living: !!p.is_living,
      burial_cemetery: p.burial_cemetery || '', burial_plot: p.burial_plot || '',
      burial_place: p.burial_place || '', burial_notes: p.burial_notes || '',
    })
    setFormMode({ editing: p })
  }

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !formMode) return
    setBusy(true)
    setSaveError('')
    try {
      if (formMode.editing) {
        await api.updatePerson(formMode.editing.id, {
          ...form, is_living: form.is_living ? 1 : 0,
          expected_updated_at: formMode.editing.updated_at,
        } as Partial<Person> & { expected_updated_at?: string })
      } else {
        const created = await api.createPerson({
          tree_id: id,
          ...form,
          is_living: form.is_living ? 1 : 0,
          relation: formMode.relation ? { type: formMode.relation.type, personId: formMode.relation.personId } : undefined,
        } as Parameters<typeof api.createPerson>[0])
        if (created.possible_duplicate) {
          const d = created.possible_duplicate
          setDupNotice(`Heads-up: “${d.first_name} ${d.last_name}”${d.birth_date ? ` (b. ${d.birth_date.slice(0, 4)})` : ''} already lives in this tree — the new entry may be a duplicate. Review both and remove one if needed (removals rest in Trash for 30 days).`)
        }
      }
      setFormMode(null)
      await load()
    } catch (e) {
      if (e instanceof Error && e.message.includes('409')) {
        alert('This person was edited by someone else while you had the form open. The tree will refresh — please re-apply your change.')
        setFormMode(null)
        await load()
      } else {
        const raw = e instanceof Error ? e.message : ''
        const clean = raw.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim()
        // a plan rejection is not a failure — say which it is (D-130)
        setSaveError(raw.includes('402') || /limit of the owner/i.test(clean)
          ? `${clean} Nothing was lost — the person was simply not added.`
          : (clean || 'Could not save — please try again.'))
      }
    } finally { setBusy(false) }
  }

  const removePerson = async (p: Person) => {
    if (!confirm(`Move ${maskName(p, getVeil())} to the trash? They can be restored from ⚙ settings → Trash for 30 days.`)) return
    await api.deletePerson(p.id)
    setSelected(null)
    await load()
  }

  const uploadAvatar = async (file: File) => {
    if (!id || !selected) return
    // try/finally with no catch is how this silently did nothing: a 402 (no room)
    // or 413 (too large) rejected, React dropped the rejection on the floor, the
    // spinner stopped and the face just never changed. Third call site of the same
    // bug D-128 records, which is why D-131 says to grep the siblings (D-144).
    setBusy(true); setSaveError('')
    try {
      if (file.size > 15 * 1024 * 1024) {
        setSaveError(`That photo is ${(file.size / 1024 / 1024).toFixed(1)} MB — portraits must be under 15 MB. Nothing was uploaded.`)
        return
      }
      await api.uploadMedia({ file, filename: file.name, treeId: id, personId: selected.id, kind: 'avatar' })
      await load()
    } catch (e) {
      setSaveError(e instanceof Error
        ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim()
        : 'That portrait could not be saved — please try again.')
    } finally { setBusy(false) }
  }

  if (loading || !graph || !layout) {
    return <div className="flex items-center justify-center py-32">
      <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400" />
    </div>
  }

  const years = (p: Person) => {
    const b = p.birth_date?.slice(0, 4), d = p.death_date?.slice(0, 4)
    if (!b && !d) return ''
    return `${b || '?'} — ${p.is_living ? 'living' : (d || '?')}`
  }

  return (
    <div className="max-w-[1500px] mx-auto px-2 sm:px-4 py-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link to="/trees" className="text-slate-500 hover:text-slate-300 text-sm">←</Link>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-100 truncate">{graph.tree.name}</h1>
            {role !== 'owner' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rootv-500/15 text-rootv-400 border border-rootv-500/25">{role}</span>
            )}
          </div>
          <p className="text-xs text-slate-500 ms-6">
            {t('tree.souls', { count: graph.persons.length })} · {t('tree.bonds', { count: graph.relationships.length })} <CapacityMeter capacity={graph.capacity} />
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex rounded-xl bg-white/[0.05] border border-white/10 p-0.5">
            {([['graph', `🕸 ${t('tree.tab.graph')}`], ['chronicle', `📜 ${t('tree.tab.chronicle')}`], ['gallery', `📸 ${t('tree.tab.gallery')}`], ['atlas', `🗺 ${t('tree.tab.atlas')}`]] as const).map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`px-2.5 sm:px-3 py-1.5 rounded-[10px] text-xs font-medium whitespace-nowrap ${tab === k ? 'bg-gold-500/20 text-gold-300' : 'text-slate-400 hover:text-slate-200'}`}>
                {label}
              </button>
            ))}
          </div>
          {tab === 'graph' && (
            <button
              onClick={() => { const v = view === 'fan' ? 'constellation' : 'fan'; setView(v); localStorage.setItem('fa_view', v) }}
              className="btn-ghost !px-3 !py-1.5 text-xs"
              title={view === 'fan' ? 'Back to the constellation' : 'Fan chart — five generations of ancestors on one screen'}>
              {view === 'fan' ? '🌌' : '🌓'}
            </button>
          )}
          {tab === 'graph' && view === 'constellation' && (
            <button onClick={exportSvg} className="btn-ghost !px-3 !py-1.5 text-xs" title={t('tree.exportTitle')}>⬇</button>
          )}
          <button onClick={() => setShowSettings(true)} className="btn-ghost !px-3 !py-1.5 text-xs" title={t('tree.settingsTitle')}>⚙</button>
          {tab === 'graph' && canEdit && <button onClick={() => openAdd()} className="btn-gold !px-3 sm:!px-4 !py-1.5 text-sm whitespace-nowrap">{t('tree.addPerson')}</button>}
        </div>
      </div>

      <ClaimPrompt treeId={graph.tree.id} persons={graph.persons} onFiled={load} />

      {tab === 'graph' && view === 'constellation' && (
        <LensBar
          kind={lens}
          onKind={k => { setLens(k); localStorage.setItem(`fa_lens_${window.location.pathname}`, k) }}
          collaterals={collaterals}
          onCollaterals={v => { setCollaterals(v); localStorage.setItem('fa_lens_collaterals', v ? '1' : '0') }}
          focusName={graph.persons.find(p => p.id === activeId)?.first_name}
          shown={lensed?.persons.length ?? graph.persons.length}
          total={graph.persons.length}
        />
      )}
      <SeedNudge persons={graph.persons} canEdit={canEdit}
        photoCount={graph.persons.filter(p => p.avatar_media_id || p.photo_url).length}
        storyCount={0}
        onPick={p => { setSelected(p); setTab('graph') }} />

      {saveError && (
        <div className="flex items-start gap-2.5 mb-4 px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/[0.07] text-red-200 text-xs">
          <span className="text-sm">⚠</span>
          <p className="flex-1">{saveError}</p>
          <button onClick={() => setSaveError('')} className="text-red-400/70 hover:text-red-300">✕</button>
        </div>
      )}

      {dupNotice && (
        <div className="flex items-start gap-2.5 mb-4 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] text-amber-200 text-xs">
          <span className="text-sm">👁</span>
          <p className="flex-1">{dupNotice}</p>
          <button onClick={() => setDupNotice('')} className="text-amber-400/70 hover:text-amber-300">✕</button>
        </div>
      )}

      {tab === 'chronicle' && <ChronicleTab treeId={graph.tree.id} persons={graph.persons} onSelect={p => { setSelected(p); setTab('graph') }} />}
      {tab === 'gallery' && (
        <GalleryTab tree={graph.tree} persons={graph.persons} canEdit={canEdit} onVaultPrompt={() => setShowSettings(true)} />
      )}
      {tab === 'atlas' && <AtlasTab persons={graph.persons} />}

      {tab === 'graph' && (
      <div className={isMobile ? 'flex flex-col' : 'flex gap-4'}>
        {/* ── Constellation canvas ── */}
        <div className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'} relative`}>
          {view === 'fan' ? (
            <FanChart
              /* the WHOLE graph on purpose: a fan is already an ancestor lens by
                 construction, so handing it a lens-filtered subgraph would draw
                 "unrecorded mother" over a woman who is recorded, just not in
                 this lens — a gap the archive would then be asked to fill twice */
              persons={graph.persons}
              relationships={graph.relationships}
              rootId={activeId}
              selected={selected}
              veil={veil}
              canEdit={canEdit}
              onSelect={setSelected}
              onAddParent={addParentFor}
            />
          ) : (<>
          <svg
            ref={svgRef}
            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
            className="w-full rounded-2xl border border-white/[0.07] touch-none"
            style={{
              height: isMobile ? '52vh' : 'calc(100vh - 200px)',
              cursor: isPanning.current ? 'grabbing' : 'grab',
              background: 'radial-gradient(ellipse at 30% 20%, #161129 0%, #0d0a1c 55%, #070510 100%)',
            }}
            onWheel={handleWheel}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          >
            <defs>
              {layout.nodes.filter(n => n.person.avatar_media_id).map(n => (
                <clipPath key={`clip-${n.person.id}`} id={`avclip-${n.person.id}`}>
                  <circle cx="24" cy={NODE_H / 2} r="14" />
                </clipPath>
              ))}
            </defs>
            {/* edges */}
            <g>
              {layout.spouseEdges.map(e => (
                <g key={e.id} stroke="#d9a441" strokeOpacity="0.55">
                  <line x1={e.x1} y1={e.y - 2.5} x2={e.x2} y2={e.y - 2.5} strokeWidth="1.5" />
                  <line x1={e.x1} y1={e.y + 2.5} x2={e.x2} y2={e.y + 2.5} strokeWidth="1.5" />
                </g>
              ))}
              {layout.childEdges.map(e => {
                const midY = (e.fromY + e.toY) / 2
                return (
                  <path key={e.id}
                    d={`M ${e.fromX} ${e.fromY} V ${midY} H ${e.toX} V ${e.toY}`}
                    fill="none" stroke="#8b5cf6" strokeOpacity="0.5" strokeWidth="1.5" />
                )
              })}
            </g>

            {/* nodes */}
            {layout.nodes.map(n => {
              const p = n.person
              const isSel = selected?.id === p.id
              const ring = p.gender ? GENDER_RING[p.gender] || '#94a3b8' : '#94a3b8'
              return (
                <g key={p.id} transform={`translate(${n.x},${n.y})`}
                  onClick={e => { e.stopPropagation(); setSelected(p) }}
                  style={{ cursor: 'pointer' }} className={isSel ? 'node-pulse' : ''}>
                  <rect width={NODE_W} height={NODE_H} rx="14"
                    fill={isSel ? '#221a3d' : '#161129'}
                    stroke={isSel ? '#e8bc6a' : 'rgba(255,255,255,0.12)'}
                    strokeWidth={isSel ? 2 : 1} />
                  {p.avatar_media_id ? (
                    <g>
                      <image href={api.mediaUrl(p.avatar_media_id)} x="10" y={NODE_H / 2 - 14} width="28" height="28"
                        preserveAspectRatio="xMidYMid slice" clipPath={`url(#avclip-${p.id})`} />
                      <circle cx="24" cy={NODE_H / 2} r="14" fill="none" stroke={ring} strokeWidth="1.5" />
                    </g>
                  ) : (
                    <g>
                      <circle cx="24" cy={NODE_H / 2} r="12" fill={ring} fillOpacity="0.18" stroke={ring} strokeWidth="1.5" />
                      <text x="24" y={NODE_H / 2 + 4} textAnchor="middle" fontSize="11" fill={ring}>
                        {(p.first_name[0] || '?').toUpperCase()}{(p.last_name?.[0] || '').toUpperCase()}
                      </text>
                    </g>
                  )}
                  <text x="44" y={NODE_H / 2 - 4} fontSize="12.5" fontWeight="600" fill="#e8e4f0">
                    {maskName(p, veil).slice(0, 16)}
                  </text>
                  <text x="44" y={NODE_H / 2 + 13} fontSize="10" fill="#8d86a8">
                    {years(p) || (p.occupation || '').slice(0, 20)}
                  </text>
                  {!p.is_living && <text x={NODE_W - 14} y="18" fontSize="10" fill="#8d86a8">✝</text>}
                </g>
              )
            })}

            {graph.persons.length === 0 && (
              <text x={viewBox.x + viewBox.w / 2} y={viewBox.y + viewBox.h / 2} textAnchor="middle" fill="#8d86a8" fontSize="16">
                {t('tree.emptySky')}
              </text>
            )}
          </svg>

          {/* zoom buttons */}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5">
            <button onClick={() => fitView(graph)} className="px-2 h-8 rounded-lg bg-void-800/90 border border-white/10 text-slate-300 hover:text-gold-300 text-xs">{t('tree.fit')}</button>
            <button onClick={() => setViewBox(v => ({ x: v.x + v.w * 0.1, y: v.y + v.h * 0.1, w: v.w * 0.8, h: v.h * 0.8 }))}
              className="w-8 h-8 rounded-lg bg-void-800/90 border border-white/10 text-slate-300 hover:text-gold-300">+</button>
            <button onClick={() => setViewBox(v => ({ x: v.x - v.w * 0.125, y: v.y - v.h * 0.125, w: v.w * 1.25, h: v.h * 1.25 }))}
              className="w-8 h-8 rounded-lg bg-void-800/90 border border-white/10 text-slate-300 hover:text-gold-300">−</button>
          </div>
          </>)}
        </div>

        {/* ── Person panel ── */}
        {selected && (
          <div className={isMobile
            ? 'fixed inset-x-0 bottom-0 z-30 bg-void-900 border-t border-white/10 rounded-t-2xl max-h-[52vh] overflow-y-auto agent-enter'
            : 'w-80 flex-shrink-0'}>
            <div className={isMobile ? 'p-4' : 'card-glass p-5 sticky top-20'}>
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-3">
                  {selected.avatar_media_id ? (
                    <img src={api.mediaUrl(selected.avatar_media_id)} alt=""
                      className="w-11 h-11 rounded-full object-cover border-2 border-gold-500/40" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sm text-slate-400">
                      {(selected.first_name[0] || '?').toUpperCase()}{(selected.last_name?.[0] || '').toUpperCase()}
                    </div>
                  )}
                  <h2 className="font-display text-lg font-bold text-slate-100">{maskName(selected, veil)}{selected.claimed_by ? <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 align-middle">linked</span> : null}</h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-300 px-1">✕</button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                {years(selected)}{selected.birth_place ? ` · b. ${selected.birth_place}` : ''}
                {selected.occupation ? ` · ${selected.occupation}` : ''}
              </p>
              {canEdit && (
                <>
                  <button onClick={() => avatarRef.current?.click()} disabled={busy}
                    className="text-[11px] text-gold-400/80 hover:text-gold-300 mb-2">
                    {selected.avatar_media_id ? '↻ change avatar' : '+ add avatar'}
                  </button>
                  <input ref={avatarRef} type="file" accept="image/*" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadAvatar(f); e.target.value = '' }} />
                </>
              )}
              {selected.bio && <p className="text-sm text-slate-400 mb-3 leading-relaxed">{selected.bio}</p>}

              {/* identity linking (v0.10.0) — admin-approved, never self-serve */}
              {!selected.claimed_by && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={async () => {
                    try {
                      const r = await api.fileClaim({ tree_id: graph.tree.id, kind: 'claim', person_id: selected.id })
                      alert(r.message)
                    } catch (e) { alert(e instanceof Error && e.message.includes('409') ? 'A request is already pending for this person.' : 'Could not send the request.') }
                  }} className="text-[11px] px-2.5 py-1.5 rounded-lg border border-gold-500/30 text-gold-300 hover:bg-gold-500/10">
                    ✋ This is me
                  </button>
                  {canEdit && <LinkAccountButton treeId={graph.tree.id} personId={selected.id} />}
                </div>
              )}

              {/* Path to Paradise */}
              {!selected.is_living && (selected.burial_cemetery || selected.burial_place) && (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-gold-400/80 mb-1">🕊️ Path to Paradise</p>
                  <p className="text-sm text-slate-300">{selected.burial_cemetery}</p>
                  <p className="text-xs text-slate-500">
                    {[selected.burial_plot ? `Lot ${selected.burial_plot}` : null, selected.burial_place].filter(Boolean).join(' · ')}
                  </p>
                  {selected.burial_notes && <p className="text-xs text-slate-500 mt-1 italic">{selected.burial_notes}</p>}
                </div>
              )}

              {/* stories about this person */}
              {(() => {
                // "How am I related?" — computed locally from the graph already in
                // memory (D-110); needs the viewer to have claimed their own record.
                const mine = graph.persons.find(p => p.claimed_by && p.claimed_by === myUserId)
                if (!mine || mine.id === selected.id) return null
                const r = findRelation(mine.id, selected.id, graph.persons, graph.relationships)
                if (!r) return null
                return (
                  <div className="rounded-xl border border-rootv-500/25 bg-rootv-500/[0.06] px-3 py-2 mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">how you're related</p>
                    <p className="text-sm text-slate-200">
                      {maskName(selected, veil)} is your <b className="text-rootv-300">{r.term}</b>
                      {r.term_ar ? <span className="text-slate-400" dir="rtl"> · {r.term_ar}</span> : null}
                    </p>
                  </div>
                )
              })()}
              {lens !== 'everyone' && selected.id !== activeId && (
                <button onClick={() => setFocusOverride(selected.id)}
                  className="w-full mb-3 text-xs px-3 py-2 rounded-lg border border-rootv-500/30 text-rootv-300 hover:bg-rootv-500/10">
                  ◎ Centre the view on {selected.first_name}
                </button>
              )}
              {focusOverride && focusOverride === selected.id && (
                <button onClick={() => setFocusOverride(null)}
                  className="w-full mb-3 text-xs px-3 py-2 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5">
                  ↩ Back to centring on me
                </button>
              )}
              <StoriesSection treeId={graph.tree.id} person={selected} canEdit={canEdit} />

              {/* kin lists (with unlink; siblings are derived from shared parents — no direct bond to cut) */}
              {(['parents', 'spouses', 'children', 'siblings'] as const).map(k => selectedRels[k].length > 0 && (
                <div key={k} className="mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{k}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedRels[k].map(({ person: p, relId }) => (
                      <span key={relId} className="inline-flex items-center rounded-full bg-white/5 border border-white/10 hover:border-gold-500/40 group/kin">
                        <button onClick={() => setSelected(p)} className="text-xs ps-2.5 pe-1 py-1 text-slate-300">
                          {maskName(p, veil)}
                        </button>
                        {canEdit && k !== 'siblings' && (
                          <button onClick={() => unlink(relId, maskName(p, veil))}
                            title="Remove this bond"
                            className="text-[10px] pe-2 ps-0.5 text-slate-600 hover:text-red-400 opacity-0 group-hover/kin:opacity-100 transition-opacity">
                            ✕
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {/* actions */}
              {canEdit && (
                <>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button onClick={() => openAdd({ type: 'parent-of', personId: selected.id, label: `parent of ${maskName(selected, veil).split(' ')[0]}` })}
                      className="btn-ghost !px-2 !py-2 text-xs">+ Parent</button>
                    <button onClick={() => openAdd({ type: 'spouse-of', personId: selected.id, label: `spouse of ${maskName(selected, veil).split(' ')[0]}` })}
                      className="btn-ghost !px-2 !py-2 text-xs">+ Spouse</button>
                    <button onClick={() => openAdd({ type: 'child-of', personId: selected.id, label: `child of ${maskName(selected, veil).split(' ')[0]}` })}
                      className="btn-ghost !px-2 !py-2 text-xs">+ Child</button>
                    <button onClick={() => openAdd({ type: 'sibling-of', personId: selected.id, label: `sibling of ${maskName(selected, veil).split(' ')[0]}` })}
                      className="btn-ghost !px-2 !py-2 text-xs">+ Sibling</button>
                    <button onClick={() => openEdit(selected)} className="btn-ghost !px-2 !py-2 text-xs">✎ Edit</button>
                    <button onClick={() => setLinking(l => !l)}
                      className={`!px-2 !py-2 text-xs rounded-xl border ${linking ? 'border-rootv-500/50 bg-rootv-500/10 text-rootv-300' : 'btn-ghost'}`}>🔗 Link</button>
                  </div>
                  {linking && graph && (
                    <LinkKinPanel treeId={graph.tree.id} selected={selected} persons={graph.persons} veil={veil}
                      onDone={() => { setLinking(false); load() }} onCancel={() => setLinking(false)} />
                  )}
                  <button onClick={() => removePerson(selected)}
                    className="w-full mt-2 text-xs text-red-400/70 hover:text-red-400 py-1.5">Remove from tree</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── Add/Edit modal ── */}
      {formMode && (
        <div className="fixed inset-0 z-50 bg-void-950/85 backdrop-blur-sm flex items-center justify-center px-4 overflow-y-auto py-6"
          onClick={() => setFormMode(null)}>
          <form onSubmit={submitForm} onClick={e => e.stopPropagation()} className="card-glass !bg-void-900/95 p-6 w-full max-w-lg agent-enter my-auto">
            <h2 className="font-display text-lg font-bold text-slate-100 mb-1">
              {formMode.editing ? `Edit ${formMode.editing.first_name}` : formMode.relation ? `Add ${formMode.relation.label}` : 'Add a person'}
            </h2>
            <p className="text-xs text-slate-500 mb-4">Only a first name is required — the rest can come later.</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input required autoFocus placeholder="First name *" value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="input-dark" />
              <input placeholder="Last name" value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="input-dark" />
              <input placeholder="Maiden name" value={form.maiden_name}
                onChange={e => setForm(f => ({ ...f, maiden_name: e.target.value }))} className="input-dark" />
              <input placeholder="Alias (shown when Privacy Veil is on)" value={form.alias}
                onChange={e => setForm(f => ({ ...f, alias: e.target.value }))} className="input-dark" />
              <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} className="input-dark">
                <option value="">Gender…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
              <input placeholder="Occupation" value={form.occupation}
                onChange={e => setForm(f => ({ ...f, occupation: e.target.value }))} className="input-dark" />
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wide">Born</label>
                <input type="date" value={form.birth_date}
                  onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))} className="input-dark" />
              </div>
              <input placeholder="Birthplace" value={form.birth_place}
                onChange={e => setForm(f => ({ ...f, birth_place: e.target.value }))} className="input-dark self-end" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
              <input type="checkbox" checked={form.is_living}
                onChange={e => setForm(f => ({ ...f, is_living: e.target.checked }))} className="accent-gold-500" />
              Still living
            </label>
            {!form.is_living && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 mb-3">
                <p className="text-[10px] uppercase tracking-wider text-gold-400/80 mb-2">🕊️ Path to Paradise</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wide">Died</label>
                    <input type="date" value={form.death_date}
                      onChange={e => setForm(f => ({ ...f, death_date: e.target.value }))} className="input-dark" />
                  </div>
                  <input placeholder="Cemetery name" value={form.burial_cemetery}
                    onChange={e => setForm(f => ({ ...f, burial_cemetery: e.target.value }))} className="input-dark self-end" />
                  <input placeholder="Lot / plot #" value={form.burial_plot}
                    onChange={e => setForm(f => ({ ...f, burial_plot: e.target.value }))} className="input-dark" />
                  <input placeholder="City / place" value={form.burial_place}
                    onChange={e => setForm(f => ({ ...f, burial_place: e.target.value }))} className="input-dark" />
                </div>
                <input placeholder="Notes (section, directions, headstone…)" value={form.burial_notes}
                  onChange={e => setForm(f => ({ ...f, burial_notes: e.target.value }))} className="input-dark mt-3" />
              </div>
            )}
            <textarea placeholder="Their story… (optional)" rows={2} value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} className="input-dark mb-5 resize-none" />
            <div className="flex gap-3">
              <button type="submit" disabled={busy || !form.first_name.trim()} className="btn-gold flex-1">
                {busy ? 'Saving…' : formMode.editing ? 'Save changes' : 'Add to constellation'}
              </button>
              <button type="button" onClick={() => setFormMode(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {showSettings && graph && (
        <TreeSettings tree={graph.tree} role={role} onClose={() => setShowSettings(false)} onChanged={() => load()} />
      )}
    </div>
  )
}
